import Flickity from 'flickity';
import EventHandler from '@pixelunion/events';
import { playLoadInAnimations, resetLoadInAnimations } from '@pixelunion/load-in-animations';
import FlickityA11yPatch from '../utils/FlickityA11yPatch';

export default class DynamicSlideshow {
  constructor(section) {
    this.el = section.el;
    this.settings = section.data;
    this.paginationClicked = false;
    this.events = new EventHandler();
    this.carousel = this.el.querySelector('[data-slideshow-wrapper]');
    this.slides = this.el.querySelectorAll('[data-slideshow-slide]');
    this.pagination = this.el.querySelector('[data-slideshow-pagination]');
    this.paginationButtons = this.pagination ? this.pagination.querySelectorAll('[data-slide-button]') : null;
    this.counter = this.el.querySelector('[data-slide-counter]');
    this.autoplay = this.settings.enable_autoplay;

    if (this.carousel && this.slides.length > 1) {
      /*
        To prevent Flickity from causing layout shifts when inserting the slides,
        we'll manually set the height initially.
      */
      const initialCarouselHeight = this.carousel.getBoundingClientRect().height;
      const firstSlideHeight = initialCarouselHeight ? `${initialCarouselHeight}px` : 'auto';
      this.carousel.style.setProperty('--first-slide-height', firstSlideHeight);

      /*
        Set the default speed and friction/bounciness of the slider initially.
        If it has to be set to a specific value, set it to that.
        Otherwise, set it to any value needed on mobile.
      */
      let selectedAttraction = 0.03;
      if (this.viewportIsMobile()) {
        selectedAttraction = 0.15;
      }

      let friction = 0.3;
      if (this.viewportIsMobile()) {
        friction = 0.8;
      }

      this.flickity = new Flickity(this.carousel, {
        accessibility: false, // Flickity's inherent "accessibility" actually makes it worse
        adaptiveHeight: true,
        autoPlay: this.settings.enable_autoplay
          && !this.viewportIsMobile()
          ? this.settings.autoplay_interval * 1000 : false,
        cellAlign: 'left',
        cellSelector: '[data-slideshow-slide]',
        contain: true,
        pageDots: false,
        pauseAutoPlayOnHover: true,
        selectedAttraction,
        friction,
        wrapAround: true,
        arrowShape: {
          x0: 10,
          x1: 60,
          y1: 50,
          x2: 65,
          y2: 45,
          x3: 20,
        },
      });

      this.flickityA11yPatch = new FlickityA11yPatch(this.carousel);

      this.prevNextButtons = this.el.querySelectorAll('.flickity-prev-next-button');
      if (this.prevNextButtons.length === 2) {
        this.prevNextButtons[0].setAttribute('aria-label', this.settings.previous_text);
        this.prevNextButtons[1].setAttribute('aria-label', this.settings.next_text);
      }

      this.el.style.setProperty('--slide-text-color', `${this.slides[0].dataset.textColor}`);
      this._updateTabindex();
      this._positionNavigation();
      this._updateCounter(1);

      this.flickity.on('change', index => {
        /*
          We need to remove `--first-slide-height` but doing this in Flickity's `ready` event
          doesn't seem to help, so we'll remove it if/when the slide is changed.
          By the time a slide change occurs, Flickity will be fully loaded and we can safely
          remove this property.
        */
        this.carousel.style.removeProperty('--first-slide-height');

        // Manually play load-in animations when changing slides.
        resetLoadInAnimations(Array.from(this.slides).filter((el, i) => i !== index));
        playLoadInAnimations([this.slides[index]]);

        this.carousel.style.setProperty('--slide-text-color', `${this.slides[index].dataset.textColor}`);
        this._updatePagination();
        this._positionNavigation();
        this._updateTabindex();
        this._updateCounter(index + 1);
      });

      // On any interaction on the slideshow, we want it to pause
      this.flickity.on('pointerDown', () => this.flickity.pausePlayer());

      this.events.register(this.carousel, 'rimg:load', () => {
        this.flickity.resize();
        this._positionNavigation();
      });

      // As soon as the slideshow receives keyboard or mouse focus, we want it to pause
      this.events.register(this.carousel, 'focusin', () => this.flickity.pausePlayer());

      this.events.register(window, 'resize', () => {
        this._positionNavigation();
        if (this.viewportIsMobile()) {
          this.flickity.pausePlayer();
        }
      });

      // We want to focus on the new slide if the pagination buttons were clicked
      // If we don't wait until "settle" then the slideshow experiences a weird jump issue
      this.flickity.on('settle', index => {
        if (this.paginationClicked) {
          this.slides[index].focus();
          this.paginationClicked = false;
        }
      });

      // We had to add in our own pagination buttons since the flickity ones
      // are not elements that can be tabbed to. This replicates their behaviour.
      if (this.pagination) {
        for (let i = 0; i < this.paginationButtons.length; i++) {
          this.events.register(this.paginationButtons[i], 'click', () => {
            const index = parseInt(this.paginationButtons[i].dataset.slideButton, 10);
            this.flickity.pausePlayer();
            this.flickity.select(index, true, false);
            this.paginationClicked = true;
          });
        }
      }
    }
  }

  _updatePagination() {
    if (this.pagination) {
      for (let i = 0; i < this.paginationButtons.length; i++) {
        this.paginationButtons[i].dataset.selected = 'false';
        this.paginationButtons[i].setAttribute('aria-selected', 'false');
      }
      this.paginationButtons[this.flickity.selectedIndex].dataset.selected = 'true';
      this.paginationButtons[this.flickity.selectedIndex].setAttribute('aria-selected', 'true');
    }
  }

  _positionNavigation() {
    if (this.settings.mobile_navigation_adjust) {
      const arrowButtons = this.el.querySelectorAll('.flickity-prev-next-button');
      const currentSlideheight = this.flickity.selectedElement
        .querySelector('[data-slide-image-wrapper]').offsetHeight;

      if (arrowButtons) {
        let top = '50%';

        if (this.viewportIsMobile()) {
          top = `${currentSlideheight / 2}px`;
        }

        arrowButtons[0].style.top = top;
        arrowButtons[1].style.top = top;
      }

      if (this.pagination) {
        let top = 'auto';

        if (this.viewportIsMobile()) {
          const content = this.flickity.selectedElement.querySelector('[data-slide-content]');

          if (content) {
            top = `${currentSlideheight}px`;
          } else {
            const paginationHeight = this.pagination.offsetHeight;

            // If no content, and mobile, offset by height of dots, and the bottom gutter
            top = `${currentSlideheight - paginationHeight}px`;
          }
        }

        this.pagination.style.top = top;
      }
    }
  }

  _updateTabindex() {
    // We need to remove tabindex from the slides that are not currently
    // selected, so that slides not currently shown cannot receive keyboard focus.
    for (let i = 0; i < this.slides.length; i++) {
      const links = this.slides[i].querySelectorAll('a');
      for (let j = 0; j < links.length; j++) {
        links[j].setAttribute('tabindex', i === this.flickity.selectedIndex ? '0' : '-1');
      }
    }
  }

  // This updates the counter which is used for accessibility. This gets read out to a user
  // who uses a screen reader every time the slide changes.
  _updateCounter(index) {
    const counterText = this.counter.dataset.counterTemplate
      .replace('{{ count }}', index)
      .replace('{{ total }}', this.slides.length);
    this.counter.innerHTML = counterText;
  }

  // This method can be overridden by the theme if the breakpoint is different than 720.
  viewportIsMobile() {
    return window.innerWidth < 768;
  }

  onSectionBlockSelect(block) {
    if (!this.flickity) {
      return;
    }

    const slideIndex = block.el.dataset.slideshowSlideIndex;

    this.flickity.select(slideIndex, false, false);

    if (this.autoplay) {
      this.flickity.stopPlayer();
    }
  }

  onSectionBlockDeselect() {
    if (this.flickity && this.autoplay) {
      this.flickity.playPlayer();
    }
  }

  onSectionUnload() {
    if (this.flickity) {
      this.flickity.destroy();
    }

    if (this.flickityA11yPatch) {
      this.flickityA11yPatch.unload();
    }

    this.events.unregisterAll();
  }
}
